import {
  createUnifiedTheme,
  defaultComponentThemes,
  genPageTheme,
  pageTheme as backstagePageTheme,
  palettes,
  shapes,
  type UnifiedTheme,
} from '@backstage/theme';

/**
 * Fundação do design system do Portal DevOps — identidade Grupo Hiper Saúde.
 *
 * Fonte única de verdade dos tokens visuais (cor, tipografia, forma, spacing),
 * aplicada via tema do Backstage (novo frontend system). Sobrepõe paleta,
 * tipografia, forma e o tema de página sobre o tema `light` padrão do
 * Backstage — preserva os demais valores para não quebrar o shell.
 *
 * Valores de marca derivados dos estilos computados de grupohipersaude.com.br
 * (provisórios, a confirmar com marketing). Todos os pares texto/fundo abaixo
 * atingem contraste WCAG AA.
 *
 * Regra de uso: toda decisão visual entra por este arquivo e é consumida via
 * `makeStyles(theme => ...)` ou `theme.palette.*`. Nenhum componente define
 * cor, raio ou spacing como literal.
 *
 * Escala de spacing: base 8px do Material UI (`theme.spacing(1) === 8`).
 * Spacing entra por `makeStyles` com `theme.spacing(n)` — nunca por
 * `style={{ gap: N }}`, porque o `Box` do Material UI v4 não aceita a prop
 * `gap` (ela só existe na v5).
 */
export const PORTAL_BRAND_TOKENS = {
  color: {
    // Azul institucional (classe `bg-ghs-blue`). Como texto sobre branco: 4,94:1
    // (AA, margem estreita — não escurecer sem reverificar contraste).
    primary: '#0070D2',
    // Azul-claro de destaque (`bg-ghs-light-blue`). SOMENTE como fundo com texto
    // escuro por cima (texto branco sobre ele reprova em contraste).
    primaryLight: '#B5DDFF',
    // Cinza-grafite institucional (texto secundário / cor de apoio). 8,27:1.
    secondary: '#4F4E50',
    // Títulos. 15,7:1 sobre branco.
    heading: '#232323',
    surface: '#FFFFFF',
    // Cores de estado — ausentes na marca institucional; propostas com AA sobre
    // branco (a confirmar com produto).
    success: '#2E7D32',
    warning: '#B45300',
    error: '#C62828',
    info: '#0070D2',
  },
  // Fallback seguro já carregado pelo Backstage. `Red Hat Display` (marca) fica
  // pendente de confirmação de licenciamento antes de embarcar.
  fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
  shape: {
    borderRadius: 8,
  },
} as const;

/**
 * Apelidos semânticos de tokens da paleta, para uso onde antes havia literais.
 *
 * Nenhuma cor nova é introduzida: cada entrada aponta para um valor que já
 * existe em `PORTAL_BRAND_TOKENS` ou para uma cor derivada do tema vigente
 * (que acompanha o modo claro/escuro automaticamente).
 *
 * Recebe o tema em uso para que `surfaceMuted` siga o modo claro/escuro — é o
 * que evita a regressão do bloco de código com fundo claro fixo no tema escuro.
 */
export const portalSemanticColors = (theme: {
  palette: {
    action: { hover: string };
    success: { main: string };
    error: { main: string };
    warning: { main: string };
    text: { secondary: string };
  };
}) => ({
  /** Fundo de bloco de código/evidência e de diff. Segue o modo do tema. */
  surfaceMuted: theme.palette.action.hover,
  /** Linha/contador de adição em diff. 5,13:1 sobre branco. */
  diffAdded: theme.palette.success.main,
  /** Linha/contador de remoção em diff. 5,62:1 sobre branco. */
  diffRemoved: theme.palette.error.main,
  /** Cabeçalho de hunk (`@@`) em diff. 8,27:1 sobre branco. */
  diffContext: theme.palette.text.secondary,
  /** Check em andamento. 5,03:1 sobre branco — substitui `#ef6c00` (3,08:1). */
  statusPending: theme.palette.warning.main,
  /** Check sem resultado. 8,27:1 sobre branco — substitui `#757575` (4,61:1). */
  statusNeutral: theme.palette.text.secondary,
  /**
   * Plano de fundo rebaixado — a superfície sobre a qual outras superfícies
   * repousam (a tela do mapa do ecossistema, por exemplo).
   *
   * Existe porque `background.default` e `background.paper` são o MESMO branco
   * neste tema (ambos saem de `PORTAL_BRAND_TOKENS.color.surface`), o que dá
   * 1,00:1 entre eles. Quem pintava a tela com `default` e os nós com `paper`
   * perdeu a distinção que o desenho pressupunha, sobrando só a borda.
   *
   * Deriva do tema, como `surfaceMuted`, para acompanhar claro/escuro. Nenhum
   * HEX novo entra por aqui.
   */
  surfaceSunken: theme.palette.action.hover,
});

/**
 * Constrói o tema unificado do portal (Material UI v4/v5 via Backstage),
 * sobrepondo a paleta `light` padrão com os tokens da marca.
 */
export function createPortalTheme(): UnifiedTheme {
  const t = PORTAL_BRAND_TOKENS;
  return createUnifiedTheme({
    fontFamily: t.fontFamily,
    palette: {
      ...palettes.light,
      primary: { main: t.color.primary },
      secondary: { main: t.color.secondary },
      text: {
        primary: t.color.heading,
        secondary: t.color.secondary,
      },
      background: {
        ...palettes.light.background,
        default: t.color.surface,
        paper: t.color.surface,
      },
      success: { main: t.color.success },
      warning: { main: t.color.warning },
      error: { main: t.color.error },
      info: { main: t.color.info },
      navigation: {
        ...palettes.light.navigation,
        // O indicador vinha do teal padrão do Backstage (`#9BF0E1`). O azul-claro
        // da marca sobre o fundo `#171717` da navegação dá 12,59:1 — bem acima do
        // mínimo de 3:1 para componentes (o `primary` daria 3,63:1, sem folga).
        indicator: t.color.primaryLight,
      },
    },
    // O banner de cabeçalho das páginas vinha do gradiente roxo `purpleSky`
    // padrão do Backstage. Todas as telas do portal usam `themeId="tool"`, então
    // sobrepor apenas esse tema de página basta para a marca aparecer no elemento
    // mais proeminente de cada tela. Cor sólida (o mesmo token nas duas pontas),
    // sem inventar um segundo HEX. Texto branco sobre `#0070D2`: 4,94:1 — muito
    // acima do mínimo de 3:1 exigido para texto grande. Os demais temas de página
    // do Backstage permanecem como fallback.
    pageTheme: {
      ...backstagePageTheme,
      tool: genPageTheme({
        colors: [t.color.primary, t.color.primary],
        shape: shapes.round,
        options: { fontColor: t.color.surface },
      }),
    },
    components: {
      ...defaultComponentThemes,
      // O raio da marca era um token declarado e nunca aplicado: a interface
      // `UnifiedThemeOptions` não tem campo `shape`, então o portal renderizava
      // com o raio 4 padrão do Material UI. `components` é o único caminho que a
      // API oferece. Cada entrada preserva o override padrão do Backstage e
      // acrescenta somente o raio — nada de cor, padding, sombra ou tipografia.
      MuiPaper: {
        ...defaultComponentThemes?.MuiPaper,
        styleOverrides: {
          ...(defaultComponentThemes?.MuiPaper as { styleOverrides?: object })
            ?.styleOverrides,
          // `rounded`, e não `root`: preserva `<Paper square>` sem raio, como o
          // shell do Backstage usa em barras e na navegação.
          rounded: { borderRadius: t.shape.borderRadius },
        },
      },
      MuiCard: {
        ...defaultComponentThemes?.MuiCard,
        styleOverrides: {
          ...(
            defaultComponentThemes?.MuiCard as {
              styleOverrides?: { root?: object };
            }
          )?.styleOverrides,
          root: {
            ...(
              defaultComponentThemes?.MuiCard as {
                styleOverrides?: { root?: object };
              }
            )?.styleOverrides?.root,
            borderRadius: t.shape.borderRadius,
          },
        },
      },
      MuiButton: {
        ...defaultComponentThemes?.MuiButton,
        styleOverrides: {
          ...(defaultComponentThemes?.MuiButton as { styleOverrides?: object })
            ?.styleOverrides,
          root: { borderRadius: t.shape.borderRadius },
        },
      },
    },
  });
}
